import { locationMock } from 'src/app/common/base-test.helpers.spec';
import {
  AccountStatus,
  OrganizationLink,
  PaginatableListOfBasicUsers,
  Project,
  ProjectPermissions,
  Projects,
  UpdateProjectRequest,
  UserRelationToGroup,
} from 'src/app/swagger-api';
import { linkedOrganizationsInternalName } from './manage-project.metadata';

export const mockProject: (data?: {
  permissions?: ProjectPermissions;
  title?: string;
  linkedProjects?: Project[];
  actor_role?: UserRelationToGroup;
}) => Project = (data?: {
  permissions?: ProjectPermissions;
  title?: string;
  linkedProjects?: Project[];
  actor_role?: UserRelationToGroup;
}) => {
  const permissions: ProjectPermissions = data?.permissions ?? projectRegularUserPermissions;
  const title: string = data && data.title ? data.title : '1st medical conference';
  const linkedProjects: Project[] = data && data.linkedProjects ? data.linkedProjects : [];
  const actorRole: UserRelationToGroup = data && data.actor_role ? data.actor_role : UserRelationToGroup.Member;

  return {
    entity_id: 'adfqewfq',
    permissions: structuredClone(permissions),
    title,
    description:
      'The purpose of the conference is to discuss neurological, cardiovascular diseases. We invite scientists, applicants for degrees, practitioners, teachers, and students of higher educational institutions to participate in the conference.',
    website: 'www.example.com',
    type: 'Conference',
    funding_source: 'National Neurology Center',
    diseases: [{ entity_id: 'test-id', title: 'Cancer' }],
    specialities: [{ entity_id: 'test-id', title: 'Stroke' }],
    linked_organizations: [
      {
        organization_name: 'string',
        organization_link_kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
        organization_user_id: 'string',
      },
    ],
    location: {
      country: {
        alpha2: 'US',
        full_name: 'United States of America',
      },
      city: 'San Francisco',
      center: { lat: 37.7749295, lon: -122.4194155 },
      region: 'California',
    },
    community: { entity_id: 'RareDisease', title: 'community title', focus: 'HEALTH' },
    creator: {
      user_id: 'userid',
      entity_id: 'sfogvn',
      displayed_name: 'JohnSmith',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
      location: locationMock,
    },
    created: new Date('2025-08-05T13:06:37.496664+00:00'),
    validity_period: {
      start_date: '12/20/2019',
      end_date: '11/20/2020',
    },
    linked_projects: linkedProjects,
    actor_role: actorRole,
  };
};

export const projectLeadPermissions: ProjectPermissions = {
  view: true,
  update: true,
  add_leader: true,
  remove_leader: true,
  remove: true,
  extend: true,
  request_to_reactivate: true,
  accept_reactivation_request: true,
  deny_reactivation_request: true,
  invite_users: true,
  view_invitations: true,
  leave: false,
  accept_link_request: true,
  deny_link_request: true,
  request_to_join: false,
  view_join_requests: true,
  accept_join_request: true,
  deny_join_request: true,
  request_to_link: true,
  unlink: true,
  view_members: true,
  remove_member: true,
  view_posts: true,
  add_post: true,
};

export const projectExtendLeadPermissions: ProjectPermissions = {
  accept_join_request: false,
  accept_link_request: false,
  accept_reactivation_request: false,
  add_leader: false,
  add_post: false,
  deny_join_request: false,
  deny_link_request: false,
  deny_reactivation_request: false,
  extend: true,
  invite_users: false,
  leave: false,
  remove: false,
  remove_leader: false,
  remove_member: false,
  request_to_join: false,
  request_to_link: false,
  request_to_reactivate: false,
  unlink: false,
  update: false,
  view: true,
  view_invitations: true,
  view_join_requests: true,
  view_members: true,
  view_posts: true,
};

export const projectMemberPermissions: ProjectPermissions = {
  view: true,
  update: false,
  add_leader: false,
  remove_leader: false,
  remove: false,
  extend: false,
  request_to_reactivate: false,
  accept_reactivation_request: false,
  deny_reactivation_request: false,
  invite_users: true,
  view_invitations: false,
  leave: true,
  accept_link_request: false,
  deny_link_request: false,
  request_to_join: false,
  view_join_requests: false,
  accept_join_request: false,
  deny_join_request: false,
  request_to_link: false,
  unlink: false,
  view_members: true,
  remove_member: false,
  view_posts: true,
  add_post: true,
};

export const projectRegularUserPermissions: ProjectPermissions = {
  view: true,
  update: false,
  add_leader: false,
  remove_leader: false,
  remove: false,
  extend: false,
  request_to_reactivate: false,
  accept_reactivation_request: false,
  deny_reactivation_request: false,
  invite_users: false,
  view_invitations: false,
  leave: false,
  accept_link_request: false,
  deny_link_request: false,
  request_to_join: true,
  view_join_requests: false,
  accept_join_request: false,
  deny_join_request: false,
  request_to_link: false,
  unlink: false,
  view_members: true,
  remove_member: false,
  view_posts: true,
  add_post: false,
};

export const projectNoPermissions: ProjectPermissions = {
  view: false,
  update: false,
  add_leader: false,
  remove_leader: false,
  remove: false,
  extend: false,
  request_to_reactivate: false,
  accept_reactivation_request: false,
  deny_reactivation_request: false,
  invite_users: false,
  view_invitations: false,
  leave: false,
  accept_link_request: false,
  deny_link_request: false,
  request_to_join: false,
  view_join_requests: false,
  accept_join_request: false,
  deny_join_request: false,
  request_to_link: false,
  unlink: false,
  view_members: false,
  remove_member: false,
  view_posts: false,
  add_post: false,
};

export const mockProjects: Projects = {
  items: [
    {
      entity_id: 'adfqewfq',
      permissions: structuredClone(projectLeadPermissions),
      title: '1st medical conference',
      description:
        'The purpose of the conference is to discuss neurological, cardiovascular diseases. We invite scientists, applicants for degrees, practitioners, teachers, and students of higher educational institutions to participate in the conference.',
      website: 'www.example.com',
      type: 'Conference',
      funding_source: 'National Neurology Center',
      diseases: [{ entity_id: 'test-id', title: 'Cancer' }],
      specialities: [{ entity_id: 'test-id', title: 'Stroke' }],
      linked_organizations: [
        {
          organization_name: 'string',
          organization_link_kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
          organization_user_id: 'string',
        },
      ],
      location: {
        country: {
          alpha2: 'US',
          full_name: 'United States',
        },
        city: 'San Francisco',
        region: 'California',
        center: { lat: 37.7749295, lon: -122.4194155 },
      },
      community: { entity_id: 'RareDisease', title: 'community title', focus: 'HEALTH' },
      creator: {
        user_id: 'userid',
        displayed_name: 'JohnSmith',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
        image_url: 'http://example.com/hjhj',
      },
      created: new Date('2025-08-05T13:06:37.496664+00:00'),
      validity_period: {
        start_date: '13/20/2019',
        end_date: '11/20/2020',
      },
      linked_projects: [],
      actor_role: UserRelationToGroup.Unrelated,
    },
    {
      entity_id: 'sfbvwrvwer',
      permissions: structuredClone(projectLeadPermissions),
      title: 'Support Grant',
      description:
        'The purpose of the project to support people faced with neurological diseases to get a grant for treatment.',
      website: '',
      type: 'Treatment',
      diseases: [{ entity_id: 'test-id', title: 'Cancer' }],
      specialities: [{ entity_id: 'test-id', title: 'Stroke' }],
      funding_source: '',
      linked_organizations: [
        {
          organization_name: 'string',
          organization_link_kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
          organization_user_id: 'string',
        },
      ],
      location: {
        country: {
          alpha2: 'US',
          full_name: 'United States of America, Montana, Helena',
        },
        city: '',
        center: { lat: 46.96526, lon: -109.533691 },
      },
      creator: {
        user_id: 'userid2',
        displayed_name: 'Kate',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
      },
      created: new Date('2025-08-05T13:06:37.496664+00:00'),
      validity_period: {
        start_date: '09/01/2019',
        end_date: '06/21/2020',
      },
      actor_role: UserRelationToGroup.Unrelated,
    },
    {
      entity_id: 'jkhvsdv',
      permissions: structuredClone(projectLeadPermissions),
      title: 'Rehabilitation after ischemic stroke',
      description:
        'Stroke rehabilitation is an important part of recovery after stroke. The goal of stroke rehabilitation is to help you relearn skills you lost when a stroke affected part of your brain. Stroke rehabilitation can help you regain independence and improve your quality of life.',
      website: '',
      type: 'Rehabilitation',
      diseases: [{ entity_id: 'test-id', title: 'Cancer' }],
      specialities: [{ entity_id: 'test-id', title: 'Stroke' }],
      funding_source: '',
      linked_organizations: [
        {
          organization_name: 'string',
          organization_link_kind: OrganizationLink.OrganizationLinkKindEnum.CUSTOM,
          organization_user_id: 'string',
        },
      ],
      location: {
        country: {
          alpha2: 'US',
          full_name: 'United States',
        },
        city: 'San Francisco',
        region: 'California',
        center: { lat: 37.7749295, lon: -122.4194155 },
      },
      creator: {
        user_id: 'userid3',
        displayed_name: 'Dr.John',
        is_admin: false,
        account_status: AccountStatus.ACTIVE,
      },
      created: new Date('2025-08-05T13:06:37.496664+00:00'),
      validity_period: {
        start_date: '07/09/2019',
        end_date: '03/12/2019',
      },
      actor_role: UserRelationToGroup.Unrelated,
    },
  ],
  total_count: 3,
};

export const projectsList: () => Project[] = () =>
  structuredClone(mockProjects.items)
    .concat(structuredClone(mockProjects.items))
    .concat(structuredClone(mockProjects.items))
    .concat(structuredClone(mockProjects.items));

export const mockMembersList: PaginatableListOfBasicUsers = {
  items: [
    {
      user_id: 'userid1',
      displayed_name: 'Morty1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid2',
      displayed_name: 'StiveJobs1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid3',
      displayed_name: 'ArnoldShwartz1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid4',
      displayed_name: 'AntonyHopkins1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid5',
      displayed_name: 'MarkTwen1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid6',
      displayed_name: 'SantaClous1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid7',
      displayed_name: 'Patric1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid8',
      displayed_name: 'AgentSmith1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
    {
      user_id: 'userid9',
      displayed_name: 'Trinity1',
      is_admin: false,
      account_status: AccountStatus.ACTIVE,
    },
  ],
  total_count: 9,
};

export const organizationLinks: OrganizationLink[] = [
  {
    organization_name: 'organization_id_1',
    organization_link_kind: 'CUSTOM',
  },
  {
    organization_name: 'organization_id_2',
    organization_link_kind: 'CUSTOM',
  },
];

export const excessiveUpdateProjectRequest: () => UpdateProjectRequest = () => ({
  title: 'Project title',
  description: 'Description ',
  disease_ids: ['test-id'],
  start_date: '10/7/2020',
  end_date: '10/8/2020',
  linked_projects: ['f24df0a02911445c803242df573eddd9'],
  location: {
    center: { lat: 52.3675734, lon: 4.9041389 },
    city: 'London',
    country: {
      alpha2: 'UK',
      full_name: 'England',
    },
  },
  [linkedOrganizationsInternalName]: organizationLinks,
  specialty_ids: ['test-id'],
  type: 'example type',
  website: '',
  funding_source: 'example Funding Source',
  community_id: '',
  validity_period: undefined,
});

export const apiEntity: () => object = () => {
  const data: UpdateProjectRequest = excessiveUpdateProjectRequest();
  return {
    title: data.title,
    description: data.description,
    disease_ids: data.disease_ids,
    specialty_ids: data.specialty_ids,
    linked_projects: data.linked_projects,
    location: data.location,
    validity_period: {
      start_date: '10/7/2020',
      end_date: '10/8/2020',
    },
    type: data.type,
    website: data.website,
    funding_source: data.funding_source,
    community_id: data.community_id,
    linked_organizations: data[linkedOrganizationsInternalName],
  };
};
